import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import type { User, LoginRequest, RegisterRequest } from '@/types/auth'
import { authApi } from '@/services/api/auth'
import type { SmsLoginRequest } from '@/types/auth'
// 移除未使用的 http 导入

// 定义认证状态接口
interface AuthState {
  // 用户信息
  user: User | null
  // 访问令牌
  token: string | null
  // 刷新令牌
  refreshToken: string | null
  // 认证状态
  isAuthenticated: boolean
  // 加载状态
  isLoading: boolean
  // 错误信息
  error: string | undefined
  // 错误代码
  errorCode: string | undefined
}

// 定义认证操作接口
interface AuthActions {
  // 登录
  login: (credentials: LoginRequest) => Promise<void>
  // 登出
  logout: () => void
  // 注册
  registerUser: (userData: RegisterRequest) => Promise<void>
  // 刷新认证令牌
  refreshAuthToken: () => Promise<void>
  // 更新用户信息
  updateUser: (userData: Partial<User>) => void
  // 设置加载状态
  setLoading: (loading: boolean) => void
  // 清除错误
  clearError: () => void
  // 设置用户
  setUser: (user: User | null) => void
  // 设置访问令牌
  setToken: (token: string | null) => void
  // 设置刷新令牌
  setRefreshToken: (refreshToken: string | null) => void
  // 设置认证状态
  setIsAuthenticated: (isAuthenticated: boolean) => void
  // 初始化认证状态（页面刷新后自动恢复）
  initializeAuth: () => Promise<void>
  // 设置错误状态
  setError: (params: { error: string; errorCode: string }) => void
}

// 组合认证状态和操作类型
type AuthStore = AuthState & AuthActions

// 创建 BroadcastChannel 用于多标签页状态同步
const BROADCAST_CHANNEL_NAME = 'auth-state-sync'
const broadcastChannel = typeof window !== 'undefined' ? new BroadcastChannel(BROADCAST_CHANNEL_NAME) : null

console.log('auth.ts: Module loading');

// 使用标准的 zustand persist 配置方式
export const useAuthStore = create<AuthStore>()(
  persist(
    (set, get) => {
      console.log('auth.ts: Store initializing');
      
      // 监听其他标签页的状态变更
      if (broadcastChannel) {
        broadcastChannel.onmessage = (event) => {
          if (event.data && event.data.type === 'AUTH_STATE_CHANGE') {
            console.log('auth.ts: Received auth state change from another tab', event.data.payload);
            set(event.data.payload)
          }
        }
      }
      
      return {
        // 初始状态
        user: null,
        token: null,
        refreshToken: null,
        isAuthenticated: false,
        isLoading: false,
        error: undefined,
        errorCode: undefined,

        // 登录
        login: async (credentials: LoginRequest) => {
          console.log('auth.ts: Login called', credentials);
          set({ isLoading: true, error: undefined, errorCode: undefined })
          
          try {
            // 调用登录API
            const response = await authApi.login(credentials)
            console.log('auth.ts: Login response received', response.data);

            // 处理响应数据结构（兼容包装在 data 字段或直接返回的情况）
            let tokenData;
            if (response.data && 'access_token' in response.data) {
              tokenData = response.data;
            } else if (response.data?.data && 'access_token' in response.data.data) {
              tokenData = response.data.data;
            } else {
              throw new Error('登录失败：返回的令牌数据格式不正确');
            }

            const { access_token: token, refresh_token: refreshToken } = tokenData as any;
            
            // 设置令牌
            set({ token, refreshToken })
            
            // 获取当前用户信息
            const userResponse = await authApi.getCurrentUser()
            console.log('auth.ts: Get user response received', userResponse.data);

            // 同样处理用户信息响应
            let userData;
            if (userResponse.data && 'id' in userResponse.data) {
              userData = userResponse.data;
            } else if (userResponse.data?.data && 'id' in userResponse.data.data) {
              userData = userResponse.data.data;
            } else {
              throw new Error('获取用户信息失败：数据格式不正确');
            }
            
            // 更新认证状态
            set({
              user: userData as User,
              isAuthenticated: true,
              isLoading: false,
            })
            
            // 通知其他标签页
            notifyOtherTabs({
              user: userData as User,
              token,
              refreshToken,
              isAuthenticated: true,
              isLoading: false,
            })
            
            console.log('auth.ts: Login successful');
          } catch (error: any) {
            console.error('auth.ts: Login error', error);
            // 处理标准化的错误对象
            let errorMessage = '登录失败'
            let errorCode = 'UNKNOWN'
            
            if (error.code && error.message) {
              // 已通过http拦截器标准化的错误
              errorMessage = error.message
              errorCode = error.code
            } else if (error.response?.data) {
              // 直接处理axios响应错误
              if (error.response.data.code) {
                errorMessage = error.response.data.message || '登录失败'
                errorCode = error.response.data.code
              } else {
                errorMessage = error.response.data.message || error.message || '登录失败'
              }
            } else {
              errorMessage = error.message || '登录失败'
            }
            
            set({
              isLoading: false,
              error: errorMessage,
              errorCode: errorCode,
            })
            throw error
          }
        },

        // 登出
        logout: () => {
          console.log('auth.ts: Logout called');
          
          // 清除认证状态
          const logoutState = {
            user: null,
            token: null,
            refreshToken: null,
            isAuthenticated: false,
            error: undefined,
            errorCode: undefined,
          }
          
          set(logoutState)
          
          // 通知其他标签页
          notifyOtherTabs(logoutState)
          
          // 清除本地存储中的认证信息
          if (typeof window !== 'undefined') {
            localStorage.removeItem('auth-storage')
          }
          
          console.log('auth.ts: Logout successful');
        },

        // 注册用户
        registerUser: async (userData: RegisterRequest) => {
          console.log('auth.ts: registerUser called', userData);
          set({ isLoading: true, error: undefined, errorCode: undefined })
          
          try {
            // 调用注册API
            await authApi.register(userData)
            
            set({ isLoading: false, error: undefined, errorCode: undefined })
            // 不返回用户数据，符合 Promise<void> 类型
          } catch (error: any) {
            console.error('auth.ts: Register error', error);
            // 处理标准化的错误对象
            let errorMessage = '注册失败'
            let errorCode = 'UNKNOWN'
            
            if (error.code && error.message) {
              // 已通过http拦截器标准化的错误
              errorMessage = error.message
              errorCode = error.code
            } else if (error.response?.data) {
              // 直接处理axios响应错误
              if (error.response.data.code) {
                errorMessage = error.response.data.message || '注册失败'
                errorCode = error.response.data.code
              } else {
                errorMessage = error.response.data.message || error.message || '注册失败'
              }
            } else {
              errorMessage = error.message || '注册失败'
            }
            
            set({
              isLoading: false,
              error: errorMessage,
              errorCode: errorCode,
            })
            throw error
          }
        },

        // 刷新令牌
      refreshAuthToken: async () => {
        console.log('auth.ts: refreshAuthToken called');
        const { refreshToken } = get()
        
        if (!refreshToken) {
          throw new Error('没有可用的刷新令牌')
        }
        
        try {
          // 调用刷新令牌API
          const response = await authApi.refreshToken(refreshToken)
          
          // 处理响应数据结构
          let tokenData;
          if (response.data && 'access_token' in response.data) {
            tokenData = response.data;
          } else if (response.data?.data && 'access_token' in response.data.data) {
            tokenData = response.data.data;
          } else {
            throw new Error('刷新令牌失败：返回的数据格式不正确');
          }

          const { access_token: newToken } = tokenData as any;
          
          // 更新令牌
          const updatedState = {
            token: newToken,
            error: undefined,
            errorCode: undefined,
          }
          
          set(updatedState)
          
          // 通知其他标签页
          notifyOtherTabs(updatedState)
          
          console.log('auth.ts: Token refreshed successfully');
        } catch (error: any) {
          console.error('auth.ts: Refresh token error', error);
          // 处理标准化的错误对象
          let errorMessage = '刷新令牌失败'
          let errorCode = 'UNKNOWN'
          
          if (error.code && error.message) {
            // 已通过http拦截器标准化的错误
            errorMessage = error.message
            errorCode = error.code
          } else if (error.response?.data) {
            // 直接处理axios响应错误
            if (error.response.data.code) {
              errorMessage = error.response.data.message || '刷新令牌失败'
              errorCode = error.response.data.code
            } else {
              errorMessage = error.response.data.message || error.message || '刷新令牌失败'
            }
          } else {
            errorMessage = error.message || '刷新令牌失败'
          }
          
          // 刷新失败，清除认证状态
          set({
            error: errorMessage,
            errorCode: errorCode,
          })
          // 注意：logout方法会在http.ts的响应拦截器中被调用
          throw error
        }
      },

      // 短信登录
      smsLogin: async (data: SmsLoginRequest) => {
        console.log('store/auth: smsLogin started with data', data);
        console.log('store/auth: smsLogin data type:', typeof data);
        console.log('store/auth: smsLogin has sms_code:', 'sms_code' in data);
        set({ isLoading: true, error: undefined, errorCode: undefined })
        
        try {
          console.log('store/auth: About to call authApi.smsLogin');
          // 调用authApi.smsLogin而不是直接使用http对象
          const response = await authApi.smsLogin(data);
          
          console.log('store/auth: authApi.smsLogin returned response:', response);
          
          // 修复响应数据处理逻辑
          // 后端直接返回令牌对象，而不是包装在data字段中
          if (response?.data) {
            // 检查响应是否已经是我们期望的格式（直接返回令牌对象）
            let tokenData;
            if ('access_token' in response.data && 'refresh_token' in response.data) {
              // 后端直接返回令牌对象 - 这是当前的情况
              tokenData = response.data;
            } else if (response.data.data && 'access_token' in response.data.data && 'refresh_token' in response.data.data) {
              // 后端返回包装在data字段中的令牌对象
              tokenData = response.data.data;
            } else {
              console.error('store/auth: Invalid response data structure:', response);
              throw new Error('登录失败，无效的响应数据');
            }
            
            const { access_token, refresh_token } = tokenData;
            
            // 更新认证状态
            const updatedState = {
              token: access_token as string,
              refreshToken: refresh_token as string,
              isAuthenticated: true,
              isLoading: false,
              error: undefined,
              errorCode: undefined,
            }
            
            set(updatedState as any)
            
            // 通知其他标签页
            notifyOtherTabs(updatedState as any)
            
            console.log('auth.ts: SMS login successful');
            
            // 尝试获取用户信息
            try {
              const userResponse = await authApi.getCurrentUser();
              // 同样修复用户信息响应处理
              let userData;
              if (userResponse?.data && 'id' in userResponse.data) {
                // 后端直接返回用户对象
                userData = userResponse.data;
              } else if (userResponse?.data?.data && 'id' in userResponse.data.data) {
                // 后端返回包装在data字段中的用户对象
                userData = userResponse.data.data;
              } else {
                console.error('store/auth: Invalid user response data structure:', userResponse);
                // 不抛出错误，仅记录日志，避免触发http拦截器的toast
                console.warn('store/auth: 无法获取用户信息，将在后续请求中重试');
                return updatedState;
              }
              
              const user = userData as User;
              set({ user });
              notifyOtherTabs({ user });
              console.log('auth.ts: User data fetched after SMS login');
            } catch (userError) {
              // 捕获错误但不重新抛出，避免触发http拦截器的toast
              // 用户信息获取失败不应该影响登录流程
              console.error('auth.ts: Failed to fetch user data after SMS login', userError);
              console.warn('auth.ts: 登录成功，但获取用户详细信息失败，将在后续请求中重试');
            }
            
            return updatedState;
          } else {
            console.error('store/auth: Invalid response data structure:', response);
            throw new Error('登录失败，无效的响应数据');
          }
        } catch (error: any) {
          console.error('store/auth: SMS login failed with error:', error);
          console.error('store/auth: Error details:', { message: error.message, code: error.code, stack: error.stack });
          console.error('store/auth: Error response data:', error.response?.data);
          // 处理标准化的错误对象
          let errorMessage = '短信登录失败';
          let errorCode = 'UNKNOWN';
          
          if (error.code && error.message) {
            // 已通过http拦截器标准化的错误
            errorMessage = error.message;
            errorCode = error.code;
          } else if (error.response?.data) {
            // 直接处理axios响应错误
            if (error.response.data.code) {
              errorMessage = error.response.data.message || '短信登录失败';
              errorCode = error.response.data.code;
            } else {
              errorMessage = error.response.data.message || error.message || '短信登录失败';
            }
          } else {
            errorMessage = error.message || '短信登录失败';
          }
          
          set({
            isLoading: false,
            error: errorMessage,
            errorCode: errorCode,
          });
          throw error;
        }
      },

      // 发送短信验证码
      sendSmsCode: async (data: { phone: string; purpose: string }) => {
        console.log('auth.ts: sendSmsCode called with data', data);
        const { phone, purpose } = data;
        
        try {
          // 调用发送短信验证码API
          const response = await authApi.sendSmsCode({ phone, purpose })
          
          console.log('auth.ts: SMS code sent successfully', response.data);
          return response.data
        } catch (error: any) {
          console.error('auth.ts: Failed to send SMS code', error);
          // 处理标准化的错误对象
          let errorMessage = '发送验证码失败'
          let errorCode = 'UNKNOWN'
          
          if (error.code && error.message) {
            // 已通过http拦截器标准化的错误
            errorMessage = error.message
            errorCode = error.code
          } else if (error.response?.data) {
            // 直接处理axios响应错误
            if (error.response.data.code) {
              errorMessage = error.response.data.message || '发送验证码失败'
              errorCode = error.response.data.code
            } else {
              errorMessage = error.response.data.message || error.message || '发送验证码失败'
            }
          } else {
            errorMessage = error.message || '发送验证码失败'
          }
          
          set({
            error: errorMessage,
            errorCode: errorCode,
          })
          throw error
        }
      },

        // 更新用户信息
        updateUser: (userData: Partial<User>) => {
          console.log('auth.ts: updateUser called', userData);
          const { user } = get()
          if (user) {
            const updatedUser = { ...user, ...userData }
            set({ user: updatedUser })
            
            // 通知其他标签页
            notifyOtherTabs({ user: updatedUser })
            
            console.log('auth.ts: User updated');
          }
        },

        // 设置加载状态
        setLoading: (loading: boolean) => {
          console.log('auth.ts: setLoading called', loading);
          set({ isLoading: loading })
        },

        // 清除错误
        clearError: () => {
          console.log('auth.ts: clearError called');
          set({ error: undefined, errorCode: undefined })
        },

        // 设置用户
        setUser: (user: User | null) => {
          console.log('auth.ts: setUser called', user);
          set({ user })
          
          // 通知其他标签页
          notifyOtherTabs({ user })
        },

        // 设置访问令牌
        setToken: (token: string | null) => {
          console.log('auth.ts: setToken called', token);
          set({ token })
          
          // 通知其他标签页
          notifyOtherTabs({ token })
        },

        // 设置刷新令牌
        setRefreshToken: (refreshToken: string | null) => {
          console.log('auth.ts: setRefreshToken called', refreshToken);
          set({ refreshToken })
          
          // 通知其他标签页
          notifyOtherTabs({ refreshToken })
        },

        // 设置认证状态
        setIsAuthenticated: (isAuthenticated: boolean) => {
          console.log('auth.ts: setIsAuthenticated called', isAuthenticated);
          set({ isAuthenticated })
          
          // 通知其他标签页
          notifyOtherTabs({ isAuthenticated })
        },
        
        // 设置错误状态
        setError: (params: { error: string; errorCode: string }) => {
          console.log('auth.ts: setError called', params);
          set({
            error: params.error,
            errorCode: params.errorCode
          })
        },
        
        // 初始化认证状态
        initializeAuth: async () => {
          console.log('auth.ts: initializeAuth called');
          const { token, refreshToken, isAuthenticated, user } = get()

          // 如果已经有令牌但没有用户信息或需要重新验证，尝试获取用户信息
          if (token && (isAuthenticated || refreshToken)) {
            try {
              console.log('auth.ts: Attempting to fetch user data with existing token');
              const userResponse = await authApi.getCurrentUser()
              
              // 修复用户信息响应处理
              let userData;
              if (userResponse?.data && 'id' in userResponse.data) {
                // 后端直接返回用户对象
                userData = userResponse.data;
              } else if (userResponse?.data?.data && 'id' in userResponse.data.data) {
                // 后端返回包装在data字段中的用户对象
                userData = userResponse.data.data;
              } else {
                console.error('auth.ts: Invalid user response data structure:', userResponse);
                throw new Error('获取用户信息失败');
              }
              
              const updatedState = {
                user: userData as User,
                isAuthenticated: true,
              }
              
              set(updatedState)
              
              // 通知其他标签页
              notifyOtherTabs(updatedState)
              
              console.log('auth.ts: User data fetched successfully');
            } catch (error) {
              console.error('auth.ts: Failed to fetch user data, attempting to refresh token', error);
              
              // 如果获取用户信息失败，尝试刷新令牌
              if (refreshToken) {
                try {
                  await get().refreshAuthToken()
                  // 刷新令牌后再次尝试获取用户信息
                  const userResponse = await authApi.getCurrentUser()
                  
                  // 修复用户信息响应处理
                  let userData;
                  if (userResponse?.data && 'id' in userResponse.data) {
                    // 后端直接返回用户对象
                    userData = userResponse.data;
                  } else if (userResponse?.data?.data && 'id' in userResponse.data.data) {
                    // 后端返回包装在data字段中的用户对象
                    userData = userResponse.data.data;
                  } else {
                    console.error('auth.ts: Invalid user response data structure:', userResponse);
                    throw new Error('获取用户信息失败');
                  }
                  
                  const updatedState = {
                    user: userData as User,
                    isAuthenticated: true,
                  }
                  
                  set(updatedState)
                  
                  // 通知其他标签页
                  notifyOtherTabs(updatedState)
                  
                  console.log('auth.ts: User data fetched successfully after token refresh');
                } catch (refreshError) {
                  console.error('auth.ts: Token refresh failed, logging out', refreshError);
                  // 刷新令牌失败，清除认证状态
                  get().logout()
                }
              } else {
                console.error('auth.ts: No refresh token available, logging out');
                // 没有刷新令牌，清除认证状态
                get().logout()
              }
            }
          } else if (token && !isAuthenticated && !user) {
            // 如果有令牌但认证状态为false且没有用户信息，可能是localStorage数据不完整，尝试刷新令牌
            if (refreshToken) {
              try {
                await get().refreshAuthToken()
                const userResponse = await authApi.getCurrentUser()
                
                // 修复用户信息响应处理
                let userData;
                if (userResponse?.data && 'id' in userResponse.data) {
                  // 后端直接返回用户对象
                  userData = userResponse.data;
                } else if (userResponse?.data?.data && 'id' in userResponse.data.data) {
                  // 后端返回包装在data字段中的用户对象
                  userData = userResponse.data.data;
                } else {
                  console.error('auth.ts: Invalid user response data structure:', userResponse);
                  throw new Error('获取用户信息失败');
                }
                
                const updatedState = {
                  user: userData as User,
                  isAuthenticated: true,
                }
                
                set(updatedState)
                notifyOtherTabs(updatedState)
              } catch (error) {
                console.error('auth.ts: Failed to refresh token during initialization', error);
                get().logout()
              }
            } else {
              get().logout()
            }
          }
        },
      }
    },
    {
      name: 'auth-storage',
      // 可选：配置需要持久化的状态字段
      partialize: (state) => ({
        user: state.user,
        token: state.token,
        refreshToken: state.refreshToken,
        isAuthenticated: state.isAuthenticated
      })
    })
)


// 通知其他标签页状态变更
function notifyOtherTabs(payload: Partial<AuthState>) {

  if (broadcastChannel) {
    broadcastChannel.postMessage({
      type: 'AUTH_STATE_CHANGE',
      payload,
    })
  }
}

// 监听页面卸载事件，清理BroadcastChannel
if (typeof window !== 'undefined') {
  window.addEventListener('beforeunload', () => {
    if (broadcastChannel) {
      broadcastChannel.close()
    }
  })
}

// 添加全局状态变化监听，用于调试
useAuthStore.subscribe((newState, oldState) => {
  if (newState !== oldState) {
    console.log('auth.ts: State changed', { newState, oldState })
  }
})

// 在store创建完成后，在浏览器环境中初始化认证状态
// 检查是否在测试环境中，避免测试时自动初始化
const isTestEnvironment = typeof process !== 'undefined' && process.env.NODE_ENV === 'test'
console.log('auth.ts: Environment check', { isTestEnvironment, processEnv: typeof process !== 'undefined' ? process.env.NODE_ENV : 'undefined' })

// 直接导出isTestEnvironment以便在测试中使用
export const testEnvironment = isTestEnvironment

if (typeof window !== 'undefined' && !isTestEnvironment) {
  // 使用setTimeout确保store完全创建后再调用initializeAuth
  setTimeout(() => {
    useAuthStore.getState().initializeAuth().catch(console.error)
  }, 0)
}